import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuickCreate } from "../../next/quickCreate";
import {
  FormAlert,
  FormCard,
  FormDialog,
  FormField,
  FormGrid,
  FormSection,
  TableKit,
} from "@NP-Webber/crm-kit";
import { toClientApiError } from "../../api/httpClient";
import { platformApi, type PlatformTenant } from "../../api/platformApi";
import { formatOrganizationLabel } from "../organizations/orgDisplay";
import {
  usersApi,
  type RoleOption,
  type TenantUser,
} from "../../api/usersApi";
import { useAuth } from "../../auth/AuthProvider";
import { UiStateMessage } from "../../engines";
import type { UiLoadState } from "../../engines";
import { useTableLayoutPrefs } from "../../hooks/useTableLayoutPrefs";
import {
  BlockOutlinedIcon,
  CheckCircleOutlinedIcon,
  OpenInNewOutlinedIcon,
} from "../../ui/appIcons";
import { ListPageHeader } from "../../ui/ListPageHeader";
import { ListStatStrip } from "../../ui/ListStatStrip";
import { QuietButton } from "../../ui/QuietButton";
import { TableRowActions } from "../../ui/TableRowActions";
import { UserCard } from "./UserCard";

function roleLabel(user: TenantUser): string {
  if (user.roles.length === 0) {
    return "—";
  }
  return user.roles.map((role) => role.name).join(", ");
}

export function UsersListPage() {
  const auth = useAuth();
  const isSuperAdmin = auth.user?.platformRoles?.includes("super_admin") === true;
  const activeTenantId = auth.activeTenant?.tenantId ?? "";
  const activeTenantName = auth.activeTenant?.name ?? "";
  const { prefs, savePrefs } = useTableLayoutPrefs("users.list");

  const [platformTenants, setPlatformTenants] = useState<PlatformTenant[]>([]);
  const [viewTenantId, setViewTenantId] = useState(activeTenantId);
  const [rows, setRows] = useState<TenantUser[]>([]);
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const [state, setState] = useState<UiLoadState>("loading");
  const [error, setError] = useState<string | undefined>();
  const [cardOpen, setCardOpen] = useState(false);
  const [selected, setSelected] = useState<TenantUser | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteMode, setInviteMode] = useState(true);
  const [roleKey, setRoleKey] = useState("general_user");
  const [createTenantId, setCreateTenantId] = useState(activeTenantId);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  useQuickCreate("user", () => setShowCreate(true));
  // deep link from the global search: /users?user=<userId>
  const userParam = searchParams.get("user");
  useEffect(() => {
    if (!userParam || !rows.length) return;
    const hit = rows.find((r) => r.userId === userParam);
    if (hit) {
      setSelected(hit);
      setCardOpen(true);
    }
  }, [userParam, rows]);

  const scopeTenantId = isSuperAdmin ? viewTenantId : activeTenantId;

  useEffect(() => {
    if (!isSuperAdmin) {
      setViewTenantId(activeTenantId);
      setCreateTenantId(activeTenantId);
      return;
    }
    void (async () => {
      try {
        const items = await platformApi.listTenants();
        setPlatformTenants(items);
        setViewTenantId((current) => {
          if (current && items.some((item) => item.id === current)) {
            return current;
          }
          return activeTenantId || items[0]?.id || "";
        });
        setCreateTenantId((current) => {
          if (current && items.some((item) => item.id === current)) {
            return current;
          }
          return activeTenantId || items[0]?.id || "";
        });
      } catch {
        setPlatformTenants([]);
      }
    })();
  }, [isSuperAdmin, activeTenantId]);

  const load = useCallback(async () => {
    if (isSuperAdmin && !viewTenantId) {
      setState("empty");
      setRows([]);
      return;
    }
    setState("loading");
    setError(undefined);
    try {
      const result = await usersApi.list(isSuperAdmin ? viewTenantId : undefined);
      setRows(result.items);
      setRoleOptions(result.roleOptions);
      setRoleKey((current) => {
        if (result.roleOptions.length === 0) {
          return current;
        }
        if (result.roleOptions.some((option) => option.key === current)) {
          return current;
        }
        return result.roleOptions[0]!.key;
      });
      setState(result.items.length === 0 ? "empty" : "ready");
      setSelected((current) => {
        if (!current) {
          return null;
        }
        return (
          result.items.find((item) => item.membershipId === current.membershipId) ??
          null
        );
      });
    } catch (err) {
      const apiError = toClientApiError(err);
      if (apiError.status === 403) {
        setState("permission");
      } else {
        setError(apiError.message);
        setState("error");
      }
    }
  }, [isSuperAdmin, viewTenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const tenantNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const tenant of platformTenants) {
      map.set(tenant.id, tenant.name);
    }
    if (activeTenantId && activeTenantName) {
      map.set(activeTenantId, activeTenantName);
    }
    return map;
  }, [platformTenants, activeTenantId, activeTenantName]);

  const openUser = (row: Record<string, unknown>) => {
    const membershipId = String(row.membershipId ?? "");
    const user = rows.find((item) => item.membershipId === membershipId) ?? null;
    if (!user) {
      return true;
    }
    setSelected(user);
    setCardOpen(true);
    return true;
  };

  const columns = useMemo(
    () => [
      {
        key: "displayName",
        title: "שם",
        sortable: true,
        filterable: true,
        minWidth: 140,
      },
      {
        key: "email",
        title: "אימייל",
        sortable: true,
        filterable: true,
        minWidth: 180,
      },
      {
        key: "roleLabel",
        title: "תפקיד",
        filterable: true,
        minWidth: 120,
      },
      {
        key: "membershipStatus",
        title: "סטטוס",
        sortable: true,
        filterable: true,
        minWidth: 100,
        render: (value: unknown) => (value === "active" ? "פעיל" : "מושבת"),
      },
      {
        key: "actions",
        title: "פעולות",
        minWidth: 120,
        render: (_value: unknown, row: Record<string, unknown>) => {
          const membershipId = String(row.membershipId ?? "");
          const isActive = row.membershipStatus === "active";
          return (
            <TableRowActions>
              <QuietButton
                title="פתח"
                tone="accent"
                icon={<OpenInNewOutlinedIcon />}
                onClick={() => {
                  openUser(row);
                }}
              />
              <QuietButton
                title={isActive ? "השבתה" : "הפעלה"}
                tone={isActive ? "danger" : "accent"}
                icon={
                  isActive ? <BlockOutlinedIcon /> : <CheckCircleOutlinedIcon />
                }
                onClick={() => {
                  if (!membershipId) return;
                  void (async () => {
                    try {
                      if (isActive) {
                        await usersApi.disable(
                          membershipId,
                          isSuperAdmin ? scopeTenantId : undefined,
                        );
                      } else {
                        await usersApi.enable(
                          membershipId,
                          isSuperAdmin ? scopeTenantId : undefined,
                        );
                      }
                      await load();
                    } catch (err) {
                      setError(toClientApiError(err).message);
                      setState("error");
                    }
                  })();
                }}
              />
            </TableRowActions>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openUser/load close over rows
    [rows, isSuperAdmin, scopeTenantId],
  );

  const tableRows = rows.map((user) => ({
    ...user,
    id: user.membershipId,
    roleLabel: roleLabel(user),
  }));

  const stats = useMemo(() => {
    const active = rows.filter((u) => u.membershipStatus === "active").length;
    const disabled = rows.length - active;
    return { total: rows.length, active, disabled };
  }, [rows]);

  const filteredTableRows = useMemo(() => {
    if (!statusFilter) return tableRows;
    if (statusFilter === "active") {
      return tableRows.filter((u) => u.membershipStatus === "active");
    }
    return tableRows.filter((u) => u.membershipStatus !== "active");
  }, [tableRows, statusFilter]);

  const closeCard = () => {
    setCardOpen(false);
    setSelected(null);
  };

  const handleCreate = async () => {
    if (!displayName.trim() || !email.trim()) {
      setCreateError("יש למלא שם ואימייל.");
      return;
    }
    if (!inviteMode && !password) {
      setCreateError("יש למלא סיסמה או לבחור הזמנה.");
      return;
    }
    if (isSuperAdmin && !createTenantId) {
      setCreateError("יש לבחור ארגון יעד.");
      return;
    }
    setCreating(true);
    setCreateError(null);
    setCreateSuccess(null);
    try {
      const created = await usersApi.create({
        displayName: displayName.trim(),
        email: email.trim(),
        roleKey,
        ...(inviteMode
          ? { invite: true }
          : { password }),
        ...(isSuperAdmin ? { tenantId: createTenantId } : {}),
      });
      const targetName =
        tenantNameById.get(isSuperAdmin ? createTenantId : activeTenantId) ??
        "הארגון שנבחר";
      setShowCreate(false);
      setDisplayName("");
      setEmail("");
      setPassword("");
      setInviteMode(true);
      const tempHint = created.temporaryPassword
        ? ` · סיסמה זמנית: ${created.temporaryPassword}`
        : "";
      setCreateSuccess(`המשתמש נוסף לארגון ${targetName}${tempHint}`);
      if (isSuperAdmin && createTenantId !== viewTenantId) {
        setViewTenantId(createTenantId);
      } else {
        await load();
      }
      setSelected(created.item);
      setCardOpen(true);
    } catch (err) {
      setCreateError(toClientApiError(err).message);
    } finally {
      setCreating(false);
    }
  };

  const header = (
    <ListPageHeader
        screen
      onRefresh={() => {
        void load();
      }}
      primaryLabel="משתמש חדש"
      onPrimary={() => setShowCreate(true)}
    />
  );

  if (state === "loading" || state === "error" || state === "permission") {
    return (
      <section className="page-section page-section--list">
        {header}
        <UiStateMessage state={state} errorMessage={error} />
      </section>
    );
  }

  return (
    <section className="page-section page-section--list">
      {header}

      {isSuperAdmin ? (
        <div className="page-section__filters">
          <FormField
            label="ארגון לניהול"
            type="select"
            value={viewTenantId}
            onChange={setViewTenantId}
            options={platformTenants.map((tenant) => ({
              value: tenant.id,
              label: formatOrganizationLabel(tenant.code, tenant.name),
            }))}
          />
        </div>
      ) : null}

      {createSuccess ? <FormAlert severity="success">{createSuccess}</FormAlert> : null}

      <ListStatStrip
        title="סטטיסטיקות"
        activeKey={statusFilter}
        onSelect={setStatusFilter}
        columns={3}
        cards={[
          {
            key: null,
            label: "סה״כ משתמשים",
            value: stats.total,
            color: "#1976d2",
            iconName: "users",
          },
          {
            key: "active",
            label: "פעילים",
            value: stats.active,
            color: "#2e7d32",
            iconName: "check",
          },
          {
            key: "disabled",
            label: "מושבתים",
            value: stats.disabled,
            color: "#c62828",
            iconName: "x",
          },
        ]}
      />

      {state === "empty" ? (
        <FormAlert severity="info">עדיין אין משתמשים. צרו את הראשון.</FormAlert>
      ) : null}

      <div className="page-section__table-surface">
        <TableKit
          columns={columns}
          data={filteredTableRows as unknown as Array<Record<string, unknown>>}
          loading={false}
          clientSideMode
          emptyMessage="עדיין אין משתמשים."
          exportFileName="users"
          showExport
          showFilters
          showColumnPicker
          showAdvancedSettings
          columnOrder={(prefs.columnOrder as string[] | undefined) ?? undefined}
          onColumnOrderChange={(next: string[]) => {
            void savePrefs({ ...prefs, columnOrder: next });
          }}
          hiddenColumnKeys={(prefs.hiddenColumnKeys as string[] | undefined) ?? undefined}
          onHiddenColumnKeysChange={(next: string[]) => {
            void savePrefs({ ...prefs, hiddenColumnKeys: next });
          }}
          onRowClick={openUser}
          onRowDoubleClick={openUser}
          onOpenCard={openUser}
        />
      </div>

      <UserCard
        open={cardOpen}
        user={selected}
        roleOptions={roleOptions}
        scopeTenantId={isSuperAdmin ? scopeTenantId : undefined}
        onClose={closeCard}
        onChanged={() => {
          void load();
        }}
      />

      <FormDialog
        open={showCreate}
        onClose={() => {
          if (!creating) {
            setShowCreate(false);
            setCreateError(null);
          }
        }}
        size="md"
      >
        <FormCard
          title="יצירת משתמש"
          error={createError}
          onSave={() => {
            void handleCreate();
          }}
          onCancel={() => {
            setShowCreate(false);
            setCreateError(null);
          }}
          saveLabel="יצירה"
          cancelLabel="ביטול"
          saving={creating}
          saveDisabled={
            !displayName.trim() ||
            !email.trim() ||
            (!inviteMode && password.length < 8) ||
            (isSuperAdmin && !createTenantId)
          }
        >
          <FormSection>
            <FormGrid cols={2}>
              {isSuperAdmin ? (
                <FormField
                  label="ארגון יעד"
                  required
                  type="select"
                  value={createTenantId}
                  onChange={setCreateTenantId}
                  disabled={creating}
                  span={2}
                  options={platformTenants.map((tenant) => ({
                    value: tenant.id,
                    label: formatOrganizationLabel(tenant.code, tenant.name),
                  }))}
                  hint="מנהל המערכת חייב לבחור לאיזה ארגון לשייך את המשתמש"
                />
              ) : (
                <FormField
                  label="ארגון"
                  value={activeTenantName}
                  onChange={() => undefined}
                  disabled
                  span={2}
                  hint="המשתמש יתווסף לארגון הפעיל בלבד"
                />
              )}
              <FormField
                label="שם לתצוגה"
                required
                value={displayName}
                onChange={setDisplayName}
                disabled={creating}
              />
              <FormField
                label="אימייל"
                required
                value={email}
                onChange={setEmail}
                disabled={creating}
              />
              <FormField
                label="מצב הזמנה"
                type="select"
                value={inviteMode ? "invite" : "password"}
                onChange={(value) => setInviteMode(value === "invite")}
                disabled={creating}
                span={2}
                options={[
                  { value: "invite", label: "הזמנה — סיסמה זמנית אוטומטית" },
                  { value: "password", label: "הגדרת סיסמה ידנית" },
                ]}
                hint="בהזמנה הסיסמה הזמנית תוצג פעם אחת אחרי היצירה"
              />
              {!inviteMode ? (
                <FormField
                  label="סיסמה זמנית"
                  required
                  type="password"
                  value={password}
                  onChange={setPassword}
                  disabled={creating}
                  hint="לפחות 8 תווים"
                />
              ) : null}
              <FormField
                label="תפקיד"
                required
                type="select"
                value={roleKey}
                onChange={setRoleKey}
                disabled={creating}
                options={roleOptions.map((option) => ({
                  value: option.key,
                  label: option.name,
                }))}
              />
            </FormGrid>
          </FormSection>
        </FormCard>
      </FormDialog>
    </section>
  );
}
